import { Link } from 'react-router-dom';
import CategoryDropdown from './CategoryDropdown';
import CategoryCards from './CategoryCards';

export default function ContentSections() {
  return (
    <>
      {/* Category Navigation Section */}
      <section className="w-full px-4 md:px-6 py-12 md:py-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-3">
              <i className="ri-price-tag-3-line"></i>
              Browse Categories
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-3">
              Sugargoo Spreadsheet Categories
            </h2>
            <p className="text-sm text-foreground-600 max-w-lg mx-auto">
              Explore our curated product categories. Each category links to updated Sugargoo finds on our main spreadsheet platform.
            </p>
          </div>
          <div className="mb-10">
            <CategoryDropdown />
          </div>
          <CategoryCards />
        </div>
      </section>

      {/* What Is Section */}
      <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-3">
              <i className="ri-information-line"></i>
              Learn More
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-3">
              What Is a <span className="text-primary-500">Sugargoo Spreadsheet</span>?
            </h2>
          </div>
          <div className="bg-background-50 rounded-2xl border border-background-200/70 p-6 md:p-10">
            <div className="space-y-5 text-sm md:text-base text-foreground-700 leading-relaxed">
              <p>
                A <strong>Sugargoo Spreadsheet</strong> is a carefully curated database designed to help shoppers discover products, organize their finds, compare different options, and save significant time when using the Sugargoo platform. Rather than spending hours manually searching through countless product listings, a well-structured spreadsheet gives you instant access to verified finds organized by category, price range, and popularity.
              </p>
              <p>
                Whether you are hunting for the latest sneaker releases, premium streetwear pieces, designer accessories, or everyday essentials, a Sugargoo Spreadsheet transforms the shopping experience from overwhelming to effortless. Our database is regularly updated to reflect the newest trends and community favorites, ensuring you never miss out on hidden gems.
              </p>
              <p>
                The spreadsheet format also makes it incredibly easy to track your haul planning, compare shipping weight estimates across different items, and share your discoveries with other members of the Sugargoo community. For beginners especially, a well-organized spreadsheet serves as the perfect starting point for understanding how to navigate the Sugargoo ecosystem efficiently.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Use Section */}
      <section className="w-full px-4 md:px-6 py-12 md:py-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-3">
              <i className="ri-star-line"></i>
              Why Choose Us
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-3">
              Why Use Our Sugargoo Spreadsheet?
            </h2>
          </div>
          <div className="overflow-hidden rounded-2xl border border-background-200/70">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-background-100">
                  <th className="text-left px-5 py-4 font-semibold text-foreground-800">Feature</th>
                  <th className="text-left px-5 py-4 font-semibold text-foreground-800">Normal Searching</th>
                  <th className="text-left px-5 py-4 font-semibold text-primary-600">Sugargoo Spreadsheet</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200/70">
                <tr className="bg-background-50">
                  <td className="px-5 py-4 text-foreground-700 font-medium">Product Discovery</td>
                  <td className="px-5 py-4 text-foreground-500">Slow &amp; Manual</td>
                  <td className="px-5 py-4 text-primary-600 font-medium"><i className="ri-check-line mr-1"></i>Fast &amp; Curated</td>
                </tr>
                <tr className="bg-background-50">
                  <td className="px-5 py-4 text-foreground-700 font-medium">Product Organization</td>
                  <td className="px-5 py-4 text-foreground-500">Scattered &amp; Messy</td>
                  <td className="px-5 py-4 text-primary-600 font-medium"><i className="ri-check-line mr-1"></i>Structured &amp; Clear</td>
                </tr>
                <tr className="bg-background-50">
                  <td className="px-5 py-4 text-foreground-700 font-medium">Beginner Friendly</td>
                  <td className="px-5 py-4 text-foreground-500">Steep Learning Curve</td>
                  <td className="px-5 py-4 text-primary-600 font-medium"><i className="ri-check-line mr-1"></i>Easy To Navigate</td>
                </tr>
                <tr className="bg-background-50">
                  <td className="px-5 py-4 text-foreground-700 font-medium">Information Freshness</td>
                  <td className="px-5 py-4 text-foreground-500">Often Outdated</td>
                  <td className="px-5 py-4 text-primary-600 font-medium"><i className="ri-check-line mr-1"></i>Regularly Updated</td>
                </tr>
                <tr className="bg-background-50">
                  <td className="px-5 py-4 text-foreground-700 font-medium">Community Feedback</td>
                  <td className="px-5 py-4 text-foreground-500">Hard To Find</td>
                  <td className="px-5 py-4 text-primary-600 font-medium"><i className="ri-check-line mr-1"></i>Built-In Reviews</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Review Section */}
      <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-3">
              <i className="ri-chat-quote-line"></i>
              Sugargoo Review
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-3">
              Sugargoo Review: Pros, Cons &amp; Experience
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="bg-background-50 rounded-2xl border border-background-200/70 p-6 md:p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-thumb-up-line text-accent-600 text-lg"></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-950">Pros</h3>
              </div>
              <ul className="space-y-3">
                {[
                  'Extensive product variety across hundreds of categories',
                  'Reliable warehouse service with quality control photos',
                  'International shipping options to over 150 countries',
                  'Competitive agent fees compared to alternatives',
                  'User-friendly interface with detailed order tracking',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-foreground-700">
                    <i className="ri-check-line text-accent-500 mt-0.5 flex-shrink-0"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-background-50 rounded-2xl border border-background-200/70 p-6 md:p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center">
                  <i className="ri-error-warning-line text-secondary-600 text-lg"></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-950">Cons</h3>
              </div>
              <ul className="space-y-3">
                {[
                  'Shipping costs can vary significantly by destination and method',
                  'Quality checking requires attention to detail from buyers',
                  'Some shipping lines have longer processing times during peak seasons',
                  'International customs fees may apply depending on your country',
                  'Platform exchange rates may differ from market rates',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-foreground-700">
                    <i className="ri-close-line text-secondary-500 mt-0.5 flex-shrink-0"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Shipping Section */}
      <section className="w-full px-4 md:px-6 py-12 md:py-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-3">
              <i className="ri-ship-line"></i>
              Shipping Guide
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-3">
              Sugargoo Shipping Cost Guide
            </h2>
            <p className="text-sm text-foreground-600 max-w-lg mx-auto">
              Understanding shipping options helps you budget your haul accurately. Here is a breakdown of the most common shipping methods.
            </p>
          </div>
          <div className="overflow-hidden rounded-2xl border border-background-200/70">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-background-100">
                  <th className="text-left px-5 py-4 font-semibold text-foreground-800">Shipping Method</th>
                  <th className="text-left px-5 py-4 font-semibold text-foreground-800">Estimated Speed</th>
                  <th className="text-left px-5 py-4 font-semibold text-foreground-800">Cost Level</th>
                  <th className="text-left px-5 py-4 font-semibold text-foreground-800">Best For</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200/70">
                <tr className="bg-background-50">
                  <td className="px-5 py-4 text-foreground-800 font-medium">Express (DHL / FedEx)</td>
                  <td className="px-5 py-4">
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">5-10 Days</span>
                  </td>
                  <td className="px-5 py-4 text-foreground-600">High</td>
                  <td className="px-5 py-4 text-foreground-600 text-xs">Small hauls, urgent items</td>
                </tr>
                <tr className="bg-background-50">
                  <td className="px-5 py-4 text-foreground-800 font-medium">Air Freight (EMS / EUB)</td>
                  <td className="px-5 py-4">
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">10-20 Days</span>
                  </td>
                  <td className="px-5 py-4 text-foreground-600">Medium</td>
                  <td className="px-5 py-4 text-foreground-600 text-xs">Balanced speed &amp; cost</td>
                </tr>
                <tr className="bg-background-50">
                  <td className="px-5 py-4 text-foreground-800 font-medium">Economy (SAL / Sea)</td>
                  <td className="px-5 py-4">
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-foreground-100 text-foreground-600 text-xs font-medium">20-60 Days</span>
                  </td>
                  <td className="px-5 py-4 text-foreground-600">Low</td>
                  <td className="px-5 py-4 text-foreground-600 text-xs">Large hauls, budget shoppers</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Coupon Section */}
      <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-3">
              <i className="ri-coupon-line"></i>
              Save Money
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-3">
              Sugargoo Coupon &amp; Discount Codes
            </h2>
            <p className="text-sm text-foreground-600 max-w-lg mx-auto">
              Check out the latest available Sugargoo coupon codes and discounts to save on your next haul.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { name: 'Welcome Bonus', discount: '100 CNY Off', usage: 'New Users', expiry: 'Ongoing' },
              { name: 'Shipping Rebate', discount: 'Up to 200 CNY', usage: 'First Shipment', expiry: 'Limited' },
              { name: 'VIP Discount', discount: '3-8% Off', usage: 'VIP Members', expiry: 'Permanent' },
              { name: 'Seasonal Sale', discount: 'Extra 5%', usage: 'All Users', expiry: 'Seasonal' },
              { name: 'Referral Code', discount: '50 CNY Each', usage: 'Refer & Earn', expiry: 'Ongoing' },
              { name: 'Festival Promo', discount: 'Up to 15%', usage: 'All Orders', expiry: 'Event-Based' },
            ].map((coupon) => (
              <div key={coupon.name} className="bg-background-50 rounded-xl border border-background-200/70 p-5 hover:border-primary-200 transition-colors">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                    <i className="ri-coupon-3-line text-primary-500"></i>
                  </div>
                  <span className="font-semibold text-foreground-900 text-sm">{coupon.name}</span>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-foreground-500">Discount:</span>
                    <span className="font-semibold text-primary-600">{coupon.discount}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-foreground-500">Usage:</span>
                    <span className="text-foreground-700">{coupon.usage}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-foreground-500">Expiry:</span>
                    <span className="text-foreground-600">{coupon.expiry}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="w-full px-4 md:px-6 py-12 md:py-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-3">
              <i className="ri-shield-check-line"></i>
              Trust &amp; Safety
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-3">
              Is Sugargoo Legit?
            </h2>
          </div>
          <div className="bg-background-50 rounded-2xl border border-background-200/70 p-6 md:p-10">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[
                {
                  icon: 'ri-building-2-line',
                  title: 'Established Company',
                  desc: 'Sugargoo has been operating for years with a registered business entity, serving thousands of customers worldwide with verified transactions.',
                },
                {
                  icon: 'ri-archive-line',
                  title: 'Secure Warehouse System',
                  desc: 'All products go through a secure warehouse with professional quality control processes and photo verification before international shipping.',
                },
                {
                  icon: 'ri-customer-service-2-line',
                  title: 'Buyer Protection',
                  desc: 'The platform offers dispute resolution, return options for defective items, and customer support to protect buyer interests at every step.',
                },
                {
                  icon: 'ri-global-line',
                  title: 'Global Community Trust',
                  desc: 'A large and active international community of Sugargoo users regularly shares experiences, reviews, and transparent feedback on forums and social media.',
                },
              ].map((item) => (
                <div key={item.title} className="flex gap-4">
                  <div className="w-10 h-10 rounded-xl bg-accent-50 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-accent-500 text-lg`}></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground-900 text-sm mb-1.5">{item.title}</h4>
                    <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA & Guide Section */}
      <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-3">
            <i className="ri-book-open-line"></i>
            In-Depth Guide
          </span>
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-3">
            Want the Complete Sugargoo Spreadsheet Guide?
          </h2>
          <p className="text-sm text-foreground-600 max-w-md mx-auto mb-8">
            Dive deep into our comprehensive guide covering everything from product discovery to shipping strategies, coupon tips, and expert recommendations for getting the most out of Sugargoo.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/sugargoo-spreadsheet-guide"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-article-line text-lg"></i>
              Read Full Guide
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-external-link-line text-lg"></i>
              Visit Main Spreadsheet
            </a>
          </div>
        </div>
      </section>
    </>
  );
}